import { EvidenceType } from 'src/api';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { useGetOrganisationFeaturesQuery } from 'src/graphql';
import { Feature, FeatureFlag } from 'src/types';
import { useTranslation } from 'react-i18next';
import { ReportEvidence } from './ReportEvidence';
import { EvidenceList } from './ReportingReport.styles';
import {
  getEvidenceContent,
  getFileUploadEvidenceContent,
  getFormattedDate,
  getPerpetratorNames,
  ReportCardFullType,
} from './helpers';

export const ReportingReportView = ({
  report,
  activeTab,
}: {
  report: ReportCardFullType;
  activeTab?: string;
}) => {
  const { t } = useTranslation();
  const features = useGetOrganisationFeaturesQuery();
  const isFileDownloadDisabled = isRequiredFeatureEnabled(
    (features.data?.getOrganisationFeatures as Feature[]) || [],
    FeatureFlag.REPORTER_VIEW_EVIDENCE_DISABLED,
  );

  return (
    <EvidenceList data-testid={'evidence-list'}>
      <ReportEvidence
        evidenceTitle={t('public.Date.header')}
        evidenceContent={getFormattedDate(
          getEvidenceContent(report, EvidenceType.DATE, activeTab),
        )}
        firstEvidence
        step={1}
        isReporterViewEvidenceDisabled={isFileDownloadDisabled}
      />
      <ReportEvidence
        evidenceTitle={t('public.Time.header')}
        evidenceContent={getEvidenceContent(
          report,
          EvidenceType.TIME,
          activeTab,
        )}
        step={2}
        isReporterViewEvidenceDisabled={isFileDownloadDisabled}
      />
      <ReportEvidence
        evidenceTitle={t('public.Location.header')}
        evidenceContent={getEvidenceContent(
          report,
          EvidenceType.LOCATION,
          activeTab,
        )}
        step={3}
        data-testid="report-event-location"
        isReporterViewEvidenceDisabled={isFileDownloadDisabled}
      />

      <ReportEvidence
        evidenceTitle={t('public.Description.whatHappened')}
        evidenceContent={getEvidenceContent(
          report,
          EvidenceType.DESCRIPTION,
          activeTab,
        )}
        step={4}
        isReporterViewEvidenceDisabled={isFileDownloadDisabled}
      />

      <ReportEvidence
        evidenceTitle={t('public.Relationship.header')}
        evidenceContent={report?.relationshipType ?? ''}
        step={5}
        isReporterViewEvidenceDisabled={isFileDownloadDisabled}
      />
      <ReportEvidence
        evidenceTitle={t('public.Emotion.header')}
        evidenceContent={getEvidenceContent(
          report,
          EvidenceType.EMOTION,
          activeTab,
        )}
        step={6}
        isReporterViewEvidenceDisabled={isFileDownloadDisabled}
      />
      <ReportEvidence
        evidenceTitle={t('public.Witnesses.header')}
        evidenceContent={getEvidenceContent(
          report,
          EvidenceType.WITNESS,
          activeTab,
        )}
        step={7}
        isReporterViewEvidenceDisabled={isFileDownloadDisabled}
      />
      <ReportEvidence
        evidenceTitle={t('public.FileUpload.header')}
        evidenceContent={getFileUploadEvidenceContent(report, activeTab)}
        reportId={report.report}
        step={8}
        data-testid="report-evidence-section"
        isReporterViewEvidenceDisabled={isFileDownloadDisabled}
      />
      <ReportEvidence
        evidenceTitle={t('public.Perpetrators.header')}
        evidenceContent={getPerpetratorNames(report, activeTab)}
        lastEvidence
        step={9}
        data-testid="report-named-offenders"
        isReporterViewEvidenceDisabled={isFileDownloadDisabled}
      />
    </EvidenceList>
  );
};
